import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import { useSEO, graph, breadcrumbJsonLd, faqJsonLd } from "@/lib/seo";
import LeadForm from "@/components/common/LeadForm";
import { SectionLabel, Breadcrumbs } from "@/components/common/states";

const HERO = "https://images.pexels.com/photos/31684126/pexels-photo-31684126.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600";

const GUIDES = [
  {
    id: "west-vs-east",
    title: "West Pune vs East Pune: Where Should You Buy?",
    summary: "How Pune's two biggest growth corridors compare for end-users and investors.",
    body: [
      "West Pune — anchored by Hinjewadi, Wakad, Baner and Balewadi — is driven by the Rajiv Gandhi Infotech Park, Pune's largest IT hub. It suits buyers who want to live close to the western IT corridor and investors chasing dependable rental demand from a large tech workforce.",
      "East Pune — led by Kharadi, Viman Nagar, Wagholi and Hadapsar — is powered by EON IT Park, the World Trade Center and airport proximity. It tends to offer a wider price band, from value homes in Wagholi to premium riverside apartments in Kharadi.",
      "Rule of thumb: if your workplace or tenant pool is Hinjewadi, look West; if it's Kharadi/EON or you value airport access, look East. Both corridors have strong long-term fundamentals.",
    ],
    links: [
      { label: "Explore Wakad", to: "/locations/wakad" },
      { label: "Explore Kharadi", to: "/locations/kharadi" },
    ],
  },
  {
    id: "near-hinjewadi",
    title: "Best Areas to Buy Near Hinjewadi IT Park",
    summary: "The most practical residential options for professionals working in Hinjewadi.",
    body: [
      "Wakad is the classic choice — walking-to-short-drive distance from the IT park, with mature infrastructure and strong resale/rental liquidity.",
      "Baner and Balewadi are the premium upgrades, offering an upscale lifestyle with cafés, retail and easy expressway access to Hinjewadi.",
      "For newer, larger-format living, integrated townships within and around Hinjewadi (such as Life Republic) offer amenities-rich communities at competitive per-sq-ft pricing. The upcoming Metro Line 3 to Shivajinagar further strengthens the corridor.",
    ],
    links: [
      { label: "Property in Hinjewadi", to: "/locations/hinjewadi" },
      { label: "Property in Baner", to: "/locations/baner" },
      { label: "Property in Balewadi", to: "/locations/balewadi" },
    ],
  },
  {
    id: "invest-kharadi",
    title: "Investing Near Kharadi: What to Know",
    summary: "Why East Pune's IT and commercial hub remains an investor favourite.",
    body: [
      "Kharadi's concentration of global offices (EON IT Park, WTC, Gera Commerzone) sustains a deep tenant pool and healthy rental yields.",
      "Value-focused investors often look at neighbouring Wagholi and Manjari, where entry prices are lower but connectivity to Kharadi is improving quickly.",
      "Premium investors prefer riverside and gated developments in Kharadi itself for stronger capital appreciation. Airport proximity adds to long-term demand.",
    ],
    links: [
      { label: "Property in Kharadi", to: "/locations/kharadi" },
      { label: "Property in Wagholi", to: "/locations/wagholi" },
    ],
  },
  {
    id: "2bhk-vs-3bhk",
    title: "2 BHK vs 3 BHK in Pune: How to Decide",
    summary: "Balancing budget, family needs and resale when choosing your configuration.",
    body: [
      "A 2 BHK is the most liquid configuration in Pune — easier to buy, rent and resell, and ideal for young professionals, couples and first-time buyers.",
      "A 3 BHK suits growing families and those wanting a home office or guest room, and tends to attract higher-quality, longer-staying tenants in IT-led micro-markets.",
      "Consider total outflow (price, stamp duty, interest), not just headline price. In premium corridors like Baner and Kharadi, a well-located 2 BHK can outperform a stretched 3 BHK on both comfort and resale.",
    ],
    links: [
      { label: "Browse all projects", to: "/projects" },
      { label: "Tell us your requirement", to: "/#requirement" },
    ],
  },
];

const FAQS = [
  { q: "Which is better for investment — West or East Pune?", a: "Both are strong. West Pune (Hinjewadi/Wakad/Baner) is driven by Pune's largest IT hub; East Pune (Kharadi/Wagholi) by EON IT Park and airport proximity. Choose based on your target tenant pool and budget." },
  { q: "Where should I buy if I work in Hinjewadi?", a: "Wakad for convenience and liquidity, Baner/Balewadi for a premium lifestyle, or an integrated township near Hinjewadi for amenities-rich living." },
  { q: "Is a 2 BHK or 3 BHK a better buy in Pune?", a: "A 2 BHK is more liquid and budget-friendly; a 3 BHK suits growing families and can attract longer-staying tenants. Factor in total outflow, not just price." },
];

export default function Guides() {
  useSEO({
    title: "Pune Property Buyer Guides & Real Estate Insights",
    description: "Practical, honest guides to buying and investing in Pune real estate — West vs East Pune, best areas near Hinjewadi and Kharadi, and choosing between 2 BHK and 3 BHK.",
    path: "/guides",
    image: HERO,
    jsonLd: graph(
      breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Guides" }]),
      faqJsonLd(FAQS),
      ...GUIDES.map((g) => ({
        "@type": "Article",
        headline: g.title,
        description: g.summary,
        about: "Pune real estate",
        author: { "@type": "Organization", name: "Endeavour Castle" },
        publisher: { "@type": "Organization", name: "Endeavour Castle" },
      })),
    ),
  });

  return (
    <div>
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Pune residential skyline" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/40" />
        </div>
        <div className="relative ec-container py-16 sm:py-20">
          <div className="mb-4"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Guides" }]} /></div>
          <p className="text-accent text-sm font-semibold uppercase tracking-[0.2em] mb-3">Buyer Guides</p>
          <h1 className="font-display text-4xl sm:text-5xl text-white max-w-3xl leading-tight">Pune Real Estate Insights & Buyer Guides</h1>
          <p className="mt-4 text-white/85 text-lg max-w-2xl">Straight-talking guidance on where and what to buy across Pune — no jargon, no hype.</p>
        </div>
      </section>

      <div className="ec-container py-16 grid lg:grid-cols-[1fr_340px] gap-12">
        <div className="min-w-0 space-y-14">
          {GUIDES.map((g) => (
            <article key={g.id} id={g.id} className="scroll-mt-28" data-testid={`guide-${g.id}`}>
              <SectionLabel>Guide</SectionLabel>
              <h2 className="font-display text-2xl sm:text-3xl text-foreground leading-tight">{g.title}</h2>
              <p className="mt-2 text-muted-foreground">{g.summary}</p>
              <div className="mt-4 space-y-3 text-foreground/85 leading-relaxed">
                {g.body.map((para, i) => <p key={i}>{para}</p>)}
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                {g.links.map((l) => (
                  <Link key={l.to} to={l.to} data-testid={`guide-link-${g.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">
                    {l.label} <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                ))}
              </div>
            </article>
          ))}
        </div>

        <aside>
          <div className="lg:sticky lg:top-28 space-y-6">
            <div className="rounded-lg border border-border bg-white p-6">
              <h3 className="flex items-center gap-2 font-display text-lg text-foreground mb-3"><Compass className="h-5 w-5 text-primary" aria-hidden /> On this page</h3>
              <ul className="space-y-2 text-sm">
                {GUIDES.map((g) => (
                  <li key={g.id}><a href={`#${g.id}`} className="text-muted-foreground hover:text-primary transition-colors">{g.title}</a></li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-border bg-white p-6">
              <SectionLabel>Get Help</SectionLabel>
              <h3 className="font-display text-xl text-foreground mb-3">Tell us your requirement</h3>
              <LeadForm source="requirement" requirementMode variant="contact" compact submitLabel="Find Options For Me"
                successTitle="Thank you. We've received your requirement."
                successMessage="Our team will review your requirement and shortlist suitable options for you." />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
